import withComponentAuth from '../../hoc/withComponentAuth';
import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { IBrand } from '../../utils/entities/interface';
import { debounce } from '../../utils/helper';
import { SmartFilter } from '../../components/common/SmartFilter';
import Pagination from '../../components/atoms/Pagination';
import { headerBrandTable } from '../../utils/constants';
import {
  GETBrands,
  UploadErrorCodeExcel,
  UploadResponse,
} from '../../services/brands';
import { BrandList } from '../../components/ui/list/Brands';
import { UploadFile } from '@mui/icons-material';
import useSnackbar from '../../hooks/useSnackbar';
import { AddEditBrandModal } from '../../components/ui/add/brandModal';
import { usePaginationParams } from '../../hooks/usePaginationParams';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';

const BrandManagement = () => {
  const { page, rowsPerPage, setPage, setRowsPerPage } = usePaginationParams();
  const [brands, setBrands] = useState<IBrand[]>([]);
  const [totalBrands, setTotalBrands] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sort, setSort] = useState<'ASC' | 'DESC'>('ASC');
  const [isLoading, setIsLoading] = useState(true);
  const scrollRef = useScrollRestoration<HTMLDivElement>(!isLoading);
  const [openAddModal, setOpenAddModal] = useState<boolean>(false);

  // Fetch users whenever page, rowsPerPage, or searchQuery changes
  useEffect(() => {
    fetchBrands(page, rowsPerPage, searchQuery, sort);
  }, [page, rowsPerPage, searchQuery, sort]);

  // Fetch users from API
  const fetchBrands = async (
    page: number,
    limit: number,
    searchQuery?: string,
    sort?: 'ASC' | 'DESC',
  ) => {
    try {
      setIsLoading(true);
      const response = await GETBrands({
        page,
        limit,
        searchQuery,
        sortOrder: sort,
      });
      // Always ensure brands is an array, even if API returns undefined
      setBrands(response?.data || []);
      setTotalBrands(response?.count || 0);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      // Set default empty array on error
      setBrands([]);
      setTotalBrands(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage + 1); // API expects 1-based page index
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const refeshList = () => {
    fetchBrands(page, rowsPerPage, searchQuery);
  };

  const handleSaveRefresh = () => {
    refeshList();
  };

  // Debounced search handler
  const handleSearchRefresh = debounce((search: string) => {
    setSearchQuery(search); // Update the search query state
    setPage(1); // Reset to the first page for new search
  }, 500); // 500ms delay before search executes

  const handleFilterRefresh = () => {
    console.log('FILTER HAPPENED');
  };

  const handleSortRefresh = () => {
    setSort(sort == 'ASC' ? 'DESC' : 'ASC');
    refeshList();
  };

  const handleAddModal = () => {
    setOpenAddModal(true);
  };

  const handleUploadClose = () => {
    // Empty function for now
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      <SmartFilter
        title="Errorcode"
        createFilter={['SEARCH', 'ADD', 'UPLOAD']}
        uploadElement={<UploadBox />}
        filterHeader={'Filter Users'}
        addHeader={'Add Brand'}
        addbuttontitle="Add Brand"
        onSaved={handleSaveRefresh}
        onFiltered={handleFilterRefresh}
        onSearch={handleSearchRefresh}
        onSort={handleSortRefresh}
        onUpload={handleSaveRefresh}
        uploadHeader="Upload here"
        onNavigate={handleAddModal}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          p: 1,
        }}
      >
      <AddEditBrandModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onSaved={() => {
          handleSaveRefresh();
          setOpenAddModal(false);
        }}
      />

      <Box
        ref={scrollRef}
        sx={{
          height: '100%',
          maxHeight: '100%',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {/* FIXED: Check if brands is defined AND has length, AND not loading */}
        {!isLoading && (!brands || brands.length === 0) ? (
          <Typography
            textAlign={'center'}
            marginTop={'20%'}
            color="textSecondary"
          >
            No Brands Found
          </Typography>
        ) : (
          <BrandList
            brands={brands || []} // Pass empty array if brands is undefined
            headerCells={headerBrandTable || []}
            isLoading={isLoading}
            onUpdate={refeshList}
          />
        )}
      </Box>

      <Box display={'block'} bottom={0} right={0} mt={2}>
        <Pagination
          count={totalBrands}
          page={page - 1} // Convert 1-based to 0-based page index for the pagination component
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
      </Box>
    </Box>
  );
};

export default withComponentAuth(BrandManagement);

const UploadBox = () => {
  const [uploadResponse, setUploadResponse] = useState<UploadResponse | null>(
    null,
  );
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const { SnackbarComponent, showSnackbar } = useSnackbar();

  const handleFileUpload = async (event: any) => {
    const file = event.target.files[0];
    if (!file) return;

    setFileName(file.name);
    setIsUploading(true);

    try {
      const response = await UploadErrorCodeExcel(file);
      setUploadResponse(response);
      setUploadError(null);
      showSnackbar('Uploaded Successfully', 'success');
    } catch (error: any) {
      showSnackbar(error.message || 'Something went wrong', 'error');
      setUploadError(error.message);
      setUploadResponse(null);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px dashed #3f51b5',
        borderRadius: '8px',
        padding: '20px',
        backgroundColor: '#424242',
        width: '400px',
        margin: '20px auto',
      }}
    >
      {SnackbarComponent}
      <UploadFile sx={{ fontSize: '48px', color: '#3f51b5', mb: 2 }} />
      <Typography variant="h6" sx={{ mb: 1 }}>
        {isUploading ? 'Uploading...' : 'Upload Excel Document'}
      </Typography>

      <Button
        variant="contained"
        component="label"
        sx={{ mb: 2, backgroundColor: '#3f51b5', color: '#fff' }}
        disabled={isUploading}
      >
        {isUploading ? 'Uploading...' : 'Choose File'}
        <input
          type="file"
          hidden
          accept=".xlsx, .xls"
          onChange={handleFileUpload}
        />
      </Button>

      {fileName && !isUploading && !uploadResponse && !uploadError && (
        <Typography variant="body1" sx={{ color: '#4caf50' }}>
          Uploaded file: {fileName}
        </Typography>
      )}

      {uploadResponse && (
        <Typography variant="body1" sx={{ color: '#4caf50' }}>
          {uploadResponse.message}
          {uploadResponse.missingColumns && (
            <ul>
              {uploadResponse.missingColumns.map((col, index) => (
                <li key={index}>{col}</li>
              ))}
            </ul>
          )}
        </Typography>
      )}

      {uploadError && (
        <Typography variant="body1" sx={{ color: '#f44336' }}>
          {uploadError}
        </Typography>
      )}

      <a
        href="/dummy_error_codes.xlsx"
        download
        style={{ textDecoration: 'none' }}
      >
        <Typography variant="body2" sx={{ color: '#3f51b5' }}>
          Download Sample File
        </Typography>
      </a>
    </Box>
  );
};
